import { useState } from 'react'
import { X, ChevronLeft } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { WORKFLOWS, getWorkflow } from './workflowConfig'
import { isActiveProfile } from './constants'

const AREA_LABELS = {
  ads: 'Ads', seo: 'SEO', website: 'Website', social: 'Social', email: 'Email',
  design: 'Design', copywriting: 'Copywriting', account_management: 'Account', other: 'Other',
}
const areaLabel = a => AREA_LABELS[a] || a || '—'
const pathLabel = p => (p === 'internal_client_live' ? 'Internal → Client → Live' : 'Internal only')

export default function WorkflowBuilder({ client, clients, currentUser, profiles, onClose, onSaved }) {
  const toast = useToast()
  const [workflowId, setWorkflowId] = useState(null)
  const [selected, setSelected] = useState(new Set())          // task indices to create
  const [assignments, setAssignments] = useState({})           // { index: { assigned_to, date_due } }
  const [bulkAssignedTo, setBulkAssignedTo] = useState('')
  const [bulkDateDue, setBulkDateDue] = useState('')
  const [saving, setSaving] = useState(false)
  const [selectedClient, setSelectedClient] = useState(client || null)
  const clientPreset = !!client

  const workflow = workflowId ? getWorkflow(workflowId) : null
  const tasks = workflow?.tasks || []

  const chooseWorkflow = id => {
    const wf = getWorkflow(id)
    setWorkflowId(id)
    setSelected(new Set((wf?.tasks || []).map((_, i) => i)))   // all selected by default
    setAssignments({})
    setBulkAssignedTo('')
    setBulkDateDue('')
  }

  const back = () => { setWorkflowId(null); setSelected(new Set()); setAssignments({}) }

  const toggleSel = i => setSelected(s => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n })

  const updateAssignment = (i, field, value) =>
    setAssignments(a => ({ ...a, [i]: { ...a[i], [field]: value } }))

  const applyToAll = () => {
    if (!bulkAssignedTo && !bulkDateDue) return
    setAssignments(prev => {
      const next = { ...prev }
      for (const i of selected) {
        next[i] = {
          assigned_to: bulkAssignedTo || next[i]?.assigned_to || '',
          date_due: bulkDateDue || next[i]?.date_due || '',
        }
      }
      return next
    })
    toast('Applied to selected tasks', 'success')
  }

  const createTasks = async () => {
    if (!selectedClient || selected.size === 0) return
    setSaving(true)
    try {
      const rows = []
      tasks.forEach((t, i) => {
        if (!selected.has(i)) return
        const a = assignments[i] || {}
        rows.push({
          title: t.title,
          description: t.description || '',
          client_id: selectedClient.id,
          assigned_to: a.assigned_to || null,
          assigned_by: currentUser.id,
          date_due: a.date_due || null,
          status: 'not_started',
          service_area: t.service_area || null,
          approval_path: t.approval_path || 'internal',
          client_visible: false,
          collaborator_ids: [],
        })
      })
      const { error } = await supabase.from('tasks').insert(rows)
      if (error) throw error
      toast(`Added ${rows.length} task${rows.length === 1 ? '' : 's'} to ${selectedClient.name}`, 'success')
      onSaved && onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 720 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            {workflow ? `Run Workflow · ${workflow.label}` : 'Run Workflow'}
            <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-muted)', marginTop: 2 }}>{selectedClient?.name || 'Choose a client'}</div>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="modal-body">
          {/* STEP 0 — pick a client (skipped when launched from a client profile) */}
          {!selectedClient && (
            <div style={{ maxWidth: 380, margin: '8px auto' }}>
              <label className="form-label">Which client is this workflow for?</label>
              <select className="form-control" value="" onChange={e => {
                const c = (clients || []).find(x => x.id === e.target.value)
                if (c) setSelectedClient(c)
              }}>
                <option value="">— Choose a client —</option>
                {(clients || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* STEP 1 — pick a workflow */}
          {selectedClient && !workflow && (
            <>
              {!clientPreset && (
                <button onClick={() => { setSelectedClient(null); setWorkflowId(null) }}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                  <ChevronLeft size={14} /> {selectedClient.name} · change client
                </button>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {WORKFLOWS.map(wf => (
                <button key={wf.id} onClick={() => chooseWorkflow(wf.id)}
                  style={{ textAlign: 'left', background: '#fff', border: '1px solid var(--border-light)', borderRadius: 12, padding: 14, cursor: 'pointer' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{wf.label}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.4 }}>{wf.description}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>{wf.tasks.length} suggested task{wf.tasks.length === 1 ? '' : 's'}</div>
                </button>
              ))}
              </div>
            </>
          )}

          {/* STEP 2 — review & assign */}
          {selectedClient && workflow && (
            <>
              <button onClick={back}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', marginBottom: 12, padding: 0 }}>
                <ChevronLeft size={14} /> Back to workflows
              </button>

              {/* Bulk apply row */}
              <div style={{ background: 'var(--cream)', borderRadius: 12, padding: 12, marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                  Apply to all selected tasks
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>Assign to</label>
                    <select className="form-control" value={bulkAssignedTo} onChange={e => setBulkAssignedTo(e.target.value)}>
                      <option value="">— Choose —</option>
                      {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                    </select>
                  </div>
                  <div style={{ minWidth: 150 }}>
                    <label className="form-label" style={{ fontSize: 11 }}>Due date</label>
                    <input className="form-control" type="date" value={bulkDateDue} onChange={e => setBulkDateDue(e.target.value)} />
                  </div>
                  <button className="btn btn-sm" onClick={applyToAll} disabled={!bulkAssignedTo && !bulkDateDue}>Apply</button>
                </div>
              </div>

              {/* Task rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tasks.map((t, i) => {
                  const isSel = selected.has(i)
                  const a = assignments[i] || {}
                  return (
                    <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, border: '1px solid var(--border-light)', borderRadius: 10, background: isSel ? '#fff' : 'var(--cream)', opacity: isSel ? 1 : 0.6 }}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleSel(i)} style={{ marginTop: 3, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5 }}>{t.title}</div>
                        {t.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, lineHeight: 1.4 }}>{t.description}</div>}
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 50, background: 'var(--blush)', color: 'var(--navy)', fontWeight: 600 }}>{areaLabel(t.service_area)}</span>
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{pathLabel(t.approval_path)}</span>
                        </div>
                        {isSel && (
                          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                            <select className="form-control" style={{ flex: 1, minWidth: 150, fontSize: 12, padding: '6px 8px' }}
                              value={a.assigned_to || ''} onChange={e => updateAssignment(i, 'assigned_to', e.target.value)}>
                              <option value="">Unassigned</option>
                              {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
                            </select>
                            <input className="form-control" type="date" style={{ minWidth: 140, fontSize: 12, padding: '6px 8px' }}
                              value={a.date_due || ''} onChange={e => updateAssignment(i, 'date_due', e.target.value)} />
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          {workflow && (
            <button className="btn btn-gold" onClick={createTasks} disabled={saving || selected.size === 0}>
              {saving ? 'Creating…' : `Create ${selected.size} task${selected.size === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}